import { useCallback, useEffect, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { ResultStep } from '@/common/components/ConnectionSteps/ResultStep'
import { getApplicationToken } from '@/common/hooks/useApplicationAuth'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { useModalApplication } from '@/common/hooks/useModalApplication'
import { AuthResult } from '@/common/models/applicationConnection'
import { ApiError } from '@/lib/axios'
import { useChangeProfilePassword } from '../pages/ProfileAccess/hooks/useChangeProfilePassword'
import { PasswordChangeStep } from './ChangePasswordSteps/PasswordChangeStep'
import { ValidationStep } from './ChangePasswordSteps/ValidationStep'

type FlowState = 'validation' | 'password-change' | 'result'

export function useAccessPasswordChangeManager() {
  const { t } = useTranslation(['common', 'profile'])
  const { handleModal, updateModal, resetModal } = useModalApplication()
  const { userInfo } = useGetUserInfo()
  const { changeProfilePassword } = useChangeProfilePassword()

  const [currentState, setCurrentState] = useState<FlowState>('validation')
  const [authResult, setAuthResult] = useState<AuthResult>({
    success: false,
    loading: false,
  })
  const [authError, setAuthError] = useState<boolean>(false)
  const [friendlyMessage, setFriendlyMessage] = useState<string | null>(null)
  const [errorSource, setErrorSource] = useState<
    'validation' | 'password-change' | null
  >(null)
  const [appData, setAppData] = useState<{
    applicationAccountUserId: string
    applicationName: string
    accountName: string
  } | null>(null)
  const [validationValid, setValidationValid] = useState(false)
  const [passwordChangeValid, setPasswordChangeValid] = useState(false)

  const validationFormRef = useRef<{
    serviceSolutionsPassword?: string
  } | null>(null)

  const passwordChangeFormRef = useRef<{
    currentPassword?: string
    newPassword?: string
  } | null>(null)

  const renderStep = useCallback(() => {
    if (!appData) return null

    const stepNumber =
      currentState === 'validation'
        ? 1
        : currentState === 'password-change'
          ? 2
          : 3

    switch (currentState) {
      case 'validation':
        return (
          <ValidationStep
            appData={appData}
            stepInfo={{ current: stepNumber, total: 3 }}
            onValidationChange={setValidationValid}
            onFormChange={(values) => {
              validationFormRef.current =
                values as typeof validationFormRef.current
            }}
            context="change_password"
          />
        )
      case 'password-change':
        return (
          <PasswordChangeStep
            appData={appData}
            stepInfo={{ current: stepNumber, total: 3 }}
            onValidationChange={setPasswordChangeValid}
            onFormChange={(values) => {
              passwordChangeFormRef.current =
                values as typeof passwordChangeFormRef.current
            }}
          />
        )
      case 'result':
        return (
          <ResultStep
            appData={{
              applicationId: '',
              applicationName: appData.applicationName,
              userId: '',
            }}
            authResult={authResult}
            stepInfo={{ current: stepNumber, total: 3 }}
            authError={authError}
            namespace="password_change"
            accountName={appData.accountName}
            friendlyMessage={friendlyMessage}
          />
        )
      default:
        return null
    }
  }, [appData, currentState, authResult, authError, friendlyMessage])

  const handleNext = useCallback(async () => {
    if (!appData) return

    switch (currentState) {
      case 'validation': {
        const currentFormValues = validationFormRef.current
        if (!validationValid || !currentFormValues?.serviceSolutionsPassword) {
          return
        }

        try {
          await getApplicationToken({
            username: userInfo?.Person?.email || '',
            password: currentFormValues.serviceSolutionsPassword,
          })

          setCurrentState('password-change')
          setPasswordChangeValid(false)
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          setErrorSource('validation')
          setCurrentState('result')
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'password-change': {
        const currentFormValues = passwordChangeFormRef.current
        if (
          !passwordChangeValid ||
          !currentFormValues ||
          !currentFormValues.currentPassword ||
          !currentFormValues.newPassword
        ) {
          return
        }

        setAuthResult({ success: false, loading: true })
        setAuthError(false)
        setCurrentState('result')

        try {
          await changeProfilePassword({
            applicationAccountUsersId: appData.applicationAccountUserId,
            currentPassword: currentFormValues.currentPassword,
            newPassword: currentFormValues.newPassword,
          })

          setAuthResult({ success: true, loading: false })

          setTimeout(() => {
            resetModal()
          }, 3000)
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          setErrorSource('password-change')
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'result': {
        if (authError) {
          setCurrentState('validation')
          setAuthError(false)
          setAuthResult({ success: false, loading: false })
          setAppData(null)
          setValidationValid(false)
          setPasswordChangeValid(false)
          resetModal()
        } else {
          setCurrentState('validation')
          setAuthResult({ success: false, loading: false })
          setAppData(null)
          setValidationValid(false)
          setPasswordChangeValid(false)
          resetModal()
        }
        return
      }
    }
  }, [
    appData,
    currentState,
    validationValid,
    passwordChangeValid,
    userInfo?.Person?.email,
    resetModal,
    authError,
    changeProfilePassword,
  ])

  const getCancelButtonText = useCallback(() => {
    if (currentState === 'result' && authError)
      return t('profile:actions.back_button.text')
    if (currentState === 'password-change')
      return t('profile:actions.back_button.text')
    return undefined
  }, [currentState, authError, t])

  const handleBack = useCallback(() => {
    if (currentState === 'password-change') {
      setCurrentState('validation')
      setPasswordChangeValid(false)
    } else if (currentState === 'result' && authError) {
      if (errorSource === 'validation') {
        setCurrentState('validation')
      } else if (errorSource === 'password-change') {
        setCurrentState('password-change')
        setPasswordChangeValid(false)
      }
      setAuthError(false)
      setAuthResult({ success: false, loading: false })
      setFriendlyMessage(null)
      setErrorSource(null)
    }
  }, [currentState, authError, errorSource])

  useEffect(() => {
    if (!appData) return

    const getButtonText = () => {
      if (currentState === 'validation')
        return t('profile:actions.validate_button.text')
      if (currentState === 'password-change')
        return t('profile:actions.change_password_button.text')
      if (currentState === 'result' && authError)
        return t('profile:actions.close_button.text')
      return t('profile:actions.close_button.text')
    }

    const shouldShowButtons =
      currentState === 'result' ? !authResult.loading : true

    const isDisabled =
      currentState === 'validation'
        ? !validationValid
        : currentState === 'password-change'
          ? !passwordChangeValid
          : false

    updateModal({
      title: t('profile:access.modal.change_password.title'),
      description: renderStep(),
      confirmText: getButtonText(),
      cancelText: getCancelButtonText(),
      showButtons: shouldShowButtons,
      onConfirm: handleNext,
      onCancel: getCancelButtonText() ? handleBack : undefined,
      disabled: isDisabled,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentState,
    authResult,
    appData,
    validationValid,
    passwordChangeValid,
    authError,
    t,
    getCancelButtonText,
    handleBack,
  ])

  const handleChangeApplicationPassword = useCallback(
    async (
      applicationAccountUserId: string,
      applicationName: string,
      accountName: string,
    ) => {
      setAppData({ applicationAccountUserId, applicationName, accountName })
      setCurrentState('validation')
      setValidationValid(false)
      setPasswordChangeValid(false)
      setAuthError(false)
      setFriendlyMessage(null)
      setErrorSource(null)

      handleModal({
        title: t('profile:access.modal.change_password.title'),
        description: renderStep(),
        showButtons: true,
        onConfirm: handleNext,
      })
    },
    [handleModal, renderStep, handleNext, t],
  )

  return {
    handleChangeApplicationPassword,
  }
}
